import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, ZapIcon, UsersIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function AssessmentScorePage() {
  return <div className="min-h-screen bg-[#f8f8f8] flex flex-col font-sans" data-id="element-659">
      {/* Top Section - Deep Purple */}
      <div className="bg-[#1a0647] pt-16 pb-24 px-5 rounded-b-[40px] shadow-lg relative z-10" data-id="element-660">
        <motion.div className="max-w-md mx-auto flex flex-col items-center text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-661">
          {/* Circular Gauge */}
          <motion.div variants={itemVariants} className="relative w-48 h-48 mb-8" data-id="element-662">
            {/* Background Circle */}
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-663">
              <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" data-id="element-664" />
              {/* Progress Arc (approx 28.7%) */}
              <motion.circle cx="50" cy="50" r="45" fill="none" stroke="#0EB4CC" strokeWidth="8" strokeLinecap="round" strokeDasharray="282.7" initial={{
              strokeDashoffset: 282.7
            }} animate={{
              strokeDashoffset: 282.7 - 282.7 * 0.287
            }} transition={{
              duration: 1.5,
              ease: 'easeOut',
              delay: 0.5
            }} data-id="element-665" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-666">
              <span className="text-5xl font-bold text-white leading-none" data-id="element-667">
                287
              </span>
              <span className="text-[#0EB4CC] text-sm font-bold mt-1" data-id="element-668">
                / 1000
              </span>
            </div>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-669">
            Your Starting Point
          </motion.h1>

          <motion.p variants={itemVariants} className="text-[#d0bdf5] text-base mb-10 max-w-xs" data-id="element-670">
            This is where you are today. Caveo will walk you through the actions
            that move this number.
          </motion.p>

          <motion.div variants={itemVariants} className="w-full" data-id="element-671">
            <Link to="/dashboard" className="block w-full bg-[#8528e2] text-white font-bold py-4 rounded-xl hover:bg-[#7322c4] transition-colors shadow-lg text-lg no-underline" data-id="element-672">
              Start Your Action Plan
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom Section - Insights */}
      <div className="flex-1 px-5 pt-12 pb-16 -mt-8 relative z-0" data-id="element-673">
        <div className="max-w-4xl mx-auto" data-id="element-674">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-675">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.5
          }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-676">
              <div className="flex items-center gap-2 mb-4" data-id="element-677">
                <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center" data-id="element-678">
                  <AlertTriangleIcon size={16} className="text-red-500" data-id="element-679" />
                </div>
                <span className="text-xs font-bold text-red-500 uppercase tracking-wider" data-id="element-680">
                  Top Priority
                </span>
              </div>
              <h3 className="font-bold text-[#111111] text-lg mb-2 leading-tight" data-id="element-681">
                Shared passwords across critical accounts.
              </h3>
              <p className="text-sm text-gray-500" data-id="element-682">
                We'll walk you through setting up a password manager step by
                step.
              </p>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.9,
            duration: 0.5
          }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-683">
              <div className="flex items-center gap-2 mb-4" data-id="element-684">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center" data-id="element-685">
                  <ZapIcon size={16} className="text-green-500" data-id="element-686" />
                </div>
                <span className="text-xs font-bold text-green-600 uppercase tracking-wider" data-id="element-687">
                  Quick Win
                </span>
              </div>
              <h3 className="font-bold text-[#111111] text-lg mb-2 leading-tight" data-id="element-688">
                Enable auto-updates on 3 devices.
              </h3>
              <p className="text-sm text-gray-500" data-id="element-689">
                Takes 2 minutes and closes known security holes instantly.
              </p>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.0,
            duration: 0.5
          }} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-690">
              <div className="flex items-center gap-2 mb-4" data-id="element-691">
                <div className="w-8 h-8 rounded-full bg-[#f0ebf8] flex items-center justify-center" data-id="element-692">
                  <UsersIcon size={16} className="text-[#8528e2]" data-id="element-693" />
                </div>
                <span className="text-xs font-bold text-[#8528e2] uppercase tracking-wider" data-id="element-694">
                  Household Risk
                </span>
              </div>
              <h3 className="font-bold text-[#111111] text-lg mb-2 leading-tight" data-id="element-695">
                Your parents are most exposed to financial scams.
              </h3>
              <p className="text-sm text-gray-500" data-id="element-696">
                We'll guide them through recognizing and avoiding the most
                common scams.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}